import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import axios from "axios";
import {
  Alert,
  Box,
  Divider,
  Slide,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  paymentStyles,
  PAYMENT_ENDPOINTS,
  PAYMENT_METHODS,
} from "./payment.constants";
import { displayRazorpay } from "./razorpay";
import { is } from "../../../util/is";
import { useDomainConfig } from "../../../hooks/useDomainConfig";
import { formatNumber } from "../../../util/Display";

const paymentFormSchema = Yup.object().shape(
  {
    emailOrPhone: Yup.string(),
    amount: Yup.number()
      .required()
      .positive()
      .typeError("Amount must be integer")
      .test("is-decimal", "Please enter valid amount", (value) =>
        (value + "").match(/^\d+(\.\d{1,2})?$/)
      ),
    //     .when("minutes", (minutes, schema) => {
    //       return minutes > 0 ? schema.optional() : schema;
    //     }),
    //   minutes: Yup.number()
    //     .positive()
    //     .typeError("Minutes must be integer")
    //     .when("amount", (amount, schema) => {
    //       return amount > 0 ? schema.optional() : schema;
    //     }),
  },
  ["amount"]
);

export const PaymentForm = ({
  connector,
  chargerId,
  clientHandler,
  companyId,
  email,
  domain,
  customerName,
  submitHandler,
  paymentMethod,
}) => {
  const [error, setError] = useState(false);
  const firstInputRef = useRef();
  const [paymentId, setPaymentId] = useState("");
  const domainConfig = useDomainConfig();
  const paymentFormFormik = useFormik({
    initialValues: {
      emailOrPhone: email,
      customerName: customerName,
      amount: 0,
      minutes: 0,
      kwh: 0,
    },
    validationSchema: paymentFormSchema,
    onSubmit: (formData, { setSubmitting, resetForm }) => {
      (async function () {
        setError(false);

        try {
          // console.log("paymentMethod:", paymentMethod);
          if (!paymentMethod.id || !paymentMethod.keyId)
            throw new Error("Payment Id or keyId is not present");

          const paymentConfig = getPaymentConfig(paymentMethod);

          if (!paymentConfig) throw new Error("Payment Method not Supported");

          const { emailOrPhone } = formData;
          const isMobile = /^\d{10}$/.test(emailOrPhone);
          const email = !isMobile ? emailOrPhone : "";
          const mobile = isMobile ? emailOrPhone : "";
          
          // Prepare request data based on payment method
          let requestData = {
            ...formData,
            paymentId,
            chargerId,
            email,
            customerName,
            companyId,
            phone: mobile,
            domain,
            connectorId: !is.empty(connector?.ocpi_connector_id)
              ? connector?.ocpi_connector_id
              : connector?.connector_id,
          };

          // HDFC requires specific fields and amount in Rs only
          if (paymentConfig.method === "hdfc") {
            requestData = {
              email: email || emailOrPhone,
              phone: mobile || emailOrPhone,
              amount: formData.amount, // Amount in Rs only for HDFC
              chargerId,
              connectorId: !is.empty(connector?.ocpi_connector_id)
                ? connector?.ocpi_connector_id
                : connector?.connector_id,
              companyId,
              country: domainConfig.countryCode || "IN",
              domain,
            };
          } else {
            // For other payment methods, include currency and countryCode
            requestData.countryCode = domainConfig.countryCode;
            requestData.currency = connector?.currency;
          }

          const response = await axios(paymentConfig.url, {
            method: "post",
            data: requestData,
          });

          if (paymentConfig.method == "razorpay") {
            if (response?.data?.id) {
              displayRazorpay(
                {
                  ...formData,
                  currency: connector?.currency,
                  orderId: response?.data?.id,
                  connectorId: !is.empty(connector?.ocpi_connector_id)
                    ? connector?.ocpi_connector_id
                    : connector.connector_id,
                  chargerId,
                  companyId,
                  keyId: paymentConfig.keyId,
                },
                submitHandler
              );
            } else throw new Error("Error initating razorpay Payment");
          } else if (paymentConfig.method == "stripe") {
            if (response?.data?.status === "requires_payment_method") {
              clientHandler(true);
            } else if (response?.data?.clientId && response?.data?.paymentId) {
              clientHandler(true, response.data.clientId);
              setPaymentId(response.data.paymentId);
            } else {
              throw new Error("Error initating stripe Payment");
            }
          } else if (paymentConfig.method == "hdfc") {
            // HDFC payment handling
            // HDFC returns payment_links with web, mobile, and iframe URLs
            const hdfcResponse = response?.data;
            
            if (hdfcResponse?.payment_links) {
              // Prefer web URL for web UI, fallback to mobile or iframe
              const paymentUrl = hdfcResponse.payment_links.web || 
                                hdfcResponse.payment_links.mobile || 
                                hdfcResponse.payment_links.iframe;
              
              if (paymentUrl) {
                // Redirect to HDFC payment page
                window.location.href = paymentUrl;
              } else {
                console.error("HDFC Payment Response:", hdfcResponse);
                throw new Error("Error initiating HDFC Payment: Payment link not found in response");
              }
            } else if (hdfcResponse?.success) {
              // If payment is already successful (webhook processed)
              submitHandler({
                isDone: true,
                status: true,
                message:
                  "Payment is successful! Please wait, charger will start in a while.",
              });
            } else {
              console.error("HDFC Payment Response:", hdfcResponse);
              throw new Error("Error initiating HDFC Payment: Invalid response structure");
            }
          } else {
            throw new Error("PaymentMethod not supported");
          }
          setSubmitting(false);
          resetForm({ values: formData });
        } catch (error) {
          console.log("payment error:", error);
          setSubmitting(false);

          setError(true);
          resetForm();
        }
      })();
    },
  });

  const getPaymentConfig = (paymentMethod) => {
    let config;
    switch (paymentMethod.id) {
      case PAYMENT_METHODS.STRIPE: {
        config = {
          keyId: paymentMethod.keyId,
          url: PAYMENT_ENDPOINTS.getClientIdUrl,
          method: "stripe",
        };
        break;
      }
      case PAYMENT_METHODS.RAZORPAY: {
        config = {
          keyId: paymentMethod.keyId,
          url: PAYMENT_ENDPOINTS.getPaymentOrderUrl,
          method: "razorpay",
        };
        break;
      }
      case PAYMENT_METHODS.HDFC: {
        config = {
          keyId: paymentMethod.keyId,
          url: PAYMENT_ENDPOINTS.getHdfcCreateOrderUrl,
          method: "hdfc",
        };
        break;
      }

      default: {
        console.error(paymentMethod.id, " Not found");
      }
    }

    return config;
  };

  const {
    errors,
    touched,
    dirty,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    setValues,
    handleChange,
    values: { amount, emailOrPhone, kwh, minutes },
    isValid,
  } = paymentFormFormik;

  // const getMinutes = useCallback(
  //   (amount) => {
  //     const {
  //       fixedFeeCharging = 0,
  //       fixedFeeStarting = 0,
  //       kw,
  //       priceperKW = 0,
  //       priceperMin = 0,
  //       taxPercent = 0,
  //     } = connector ?? {};

  //     if (Number(priceperKW) === 0 || isNaN(Number(priceperKW))) {
  //       return (amount / priceperMin).toFixed(2).replace(/\.00$/, "");
  //     } else {
  //       const amountForCharging = amount * (1 - Number(taxPercent) / 100) - Number(fixedFeeCharging) - Number(fixedFeeStarting);
  //       const pricePerWatt = priceperKW / 1000;
  //       const totalWattToBeCharged = amountForCharging / pricePerWatt;
  //       const chargePerMin = (kw * 1000) / 60;
  //       setFieldValue(
  //         "kwh",
  //         totalWattToBeCharged > 0
  //           ? Number(totalWattToBeCharged.toFixed(2).replace(/\.00$/, "")) / 1000
  //           : 0
  //       );
  //       return (totalWattToBeCharged / chargePerMin)
  //         .toFixed(2)
  //         .replace(/\.00$/, "");
  //     }
  //   },
  //   [connector]
  // );

  const setValuesByAmount = useCallback(
    (amount) => {
      if (amount > 0) {
        const {
          fixedFeeCharging = 0,
          fixedFeeStarting = 0,
          priceperKW = 0,
          taxPercent = 0,
          transactionPercent = 0,
          priceperMin = 0,
        } = connector || {};

        const transactionFeesGSTTax = Number(transactionPercent) / 100;
        const transactionFeesAndAdditionTax =
          transactionFeesGSTTax * Number(amount);
        const subtractAdditionTax =
          Number(amount) - transactionFeesAndAdditionTax;
        const gstTaxcalculate = 1 + Number(taxPercent) / 100;
        const applyGstTax = subtractAdditionTax / gstTaxcalculate;
        const subtractOtherCharges =
          applyGstTax - Number(fixedFeeCharging) - Number(fixedFeeStarting);
        // Calculate KWH if price per KW is available
        if (priceperKW > 0) {
          const totalKw = subtractOtherCharges / Number(priceperKW);
          setFieldValue(
            "kwh",
            totalKw > 0 ? totalKw.toFixed(3).replace(/\.00$/, "") : 0
          );
        }

        // Calculate Minutes if price per Min is available
        if (priceperMin > 0) {
          const totalMin = subtractOtherCharges / Number(priceperMin);
          setFieldValue(
            "minutes",
            totalMin > 0 ? totalMin.toFixed(2).replace(/\.00$/, "") : 0
          );
        }
      } else {
        setFieldValue("kwh", 0);
        setFieldValue("minutes", 0);
      }
    },
    [connector]
  );

  const setValuesByMin = useCallback(
    (minutes) => {
      const {
        fixedFeeCharging = 0,
        fixedFeeStarting = 0,
        priceperMin,
        taxPercent = 0,
        transactionPercent = 0,
      } = connector || {};

      const originalAmount =
        (Number(priceperMin) * Number(minutes)).toFixed(2) +
        Number(fixedFeeCharging) +
        Number(fixedFeeStarting);
      console.log(`originalAmount: ${originalAmount}`);
      // const originalAmount = (kwh * 1) + Number(fixedFeeCharging) + Number(fixedFeeStarting);
      console.log("tax: ", (originalAmount * Number(taxPercent)) / 100);
      const amountBeforePG =
        Number(originalAmount) + (originalAmount * Number(taxPercent)) / 100;
      console.log(`amountBeforePG: ${amountBeforePG}`);
      const amount =
        Number(amountBeforePG) +
        Number((amountBeforePG * Number(transactionPercent)) / 100);
      console.log(`Amount: ${amount}`);

      if (minutes > 0) {
        setFieldValue("amount", amount.toFixed(2).replace(/\.00$/, ""));
      } else {
        setFieldValue("amount", 0);
      }
    },
    [connector]
  );

  const setValuesByKw = useCallback(
    (kwh) => {
      const {
        fixedFeeCharging = 0,
        fixedFeeStarting = 0,
        priceperKW,
        taxPercent = 0,
        transactionPercent = 0,
      } = connector || {};

      const cost = Number(kwh) * Number(priceperKW);
      const addSessionFees =
        cost + Number(fixedFeeCharging) + Number(fixedFeeStarting);
      const calculateGstAmount = 1 + Number(taxPercent) / 100;
      const applyGSTAmount = addSessionFees * calculateGstAmount;
      const onlyGSTTax = applyGSTAmount - addSessionFees;
      const calculateConveniencePercent = Number(transactionPercent) / 100;
      const calculateConvenienceFees =
        applyGSTAmount / (1 - calculateConveniencePercent);
      const onlyConvenienceFees = calculateConvenienceFees - applyGSTAmount;
      const totalCost = addSessionFees + onlyGSTTax + onlyConvenienceFees;

      if (kwh > 0) {
        setFieldValue("amount", totalCost.toFixed(2).replace(/\.00$/, ""));
      } else {
        setFieldValue("amount", 0);
      }
    },
    [connector]
  );

  useEffect(() => {
    clientHandler(false);
  }, [amount]);

  useEffect(() => {
    firstInputRef?.current?.focus();
    setValues({ amount: 0, emailOrPhone, kwh: "", minutes: "" });
  }, [connector?.connector_id]);

  const _handleChange = (e) => {
    const { name, value } = e.currentTarget;
    if (name === "minutes") {
      setValuesByMin(+value);
    }

    if (name === "amount") {
      setValuesByAmount(+value);
    }

    if (name === "kwh") {
      setValuesByKw(+value);
    }
    handleChange(e);
  };

  return (
    <Slide direction="up" in={!!email}>
      <Box className="tabs-parent" sx={paymentStyles.tabParent}>
        {error && (
          <Alert
            variant="outlined"
            severity="error"
            sx={{ marginBottom: "20px", color: "red" }}
          >
            Some Error Occured! Please try again.
          </Alert>
        )}
        {connector?.priceperKW && (
          <Typography variant="caption" mb={3}>
            Cost per KWH: {connector?.priceperKW || "N/A"}
          </Typography>
        )}

        {connector?.priceType === "min" && connector?.priceperMin ? (
          <Typography
            variant="caption"
            mb={3}
            sx={{ fontSize: "14px", color: "#76BC45" }}
          >
            Cost per hour:{" "}
            {`${formatNumber(connector.priceperMin * 60, {
              style: "currency",
              currency: domainConfig.currency,
            })}`}
          </Typography>
        ) : connector?.priceperKW ? (
          <Typography
            variant="caption"
            mb={3}
            sx={{ fontSize: "14px", color: "#76BC45" }}
          >
            Cost per KW:{" "}
            {`${formatNumber(connector.priceperKW, {
              style: "currency",
              currency: domainConfig.currency,
            })}`}
          </Typography>
        ) : null}

        <FormikProvider value={paymentFormFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <TextField
                label="Email or Phone"
                {...getFieldProps("emailOrPhone")}
                error={Boolean(touched.emailOrPhone && errors.emailOrPhone)}
                helperText={touched.emailOrPhone && errors.emailOrPhone}
                sx={{ backgroundColor: "white" }}
                disabled={true}
              />
              <Divider sx={{ margin: "20px 0" }} />
              <TextField
                required
                inputRef={firstInputRef}
                label="Enter amount to  Charge"
                {...getFieldProps("amount")}
                error={Boolean(touched.amount && errors.amount)}
                onChange={_handleChange}
                type="number"
                helperText={
                  (touched.amount && errors.amount) ||
                  `*Price is inclusive of ${
                    domainConfig.taxType || "GST"
                  } and other service charges`
                }
                sx={{ backgroundColor: "white" }}
              />
              <Divider sx={{ margin: "20px 0" }}></Divider>
              {connector?.priceperMin && (
                <TextField
                  label="Minutes to charge"
                  {...getFieldProps("minutes")}
                  onChange={_handleChange}
                  type="number"
                  sx={{ backgroundColor: "white" }}
                />
              )}
              <Divider sx={{ margin: "20px 0" }}></Divider>
              {connector?.priceperKW && (
                <TextField
                  label="Total KW to be charged"
                  {...getFieldProps("kwh")}
                  onChange={_handleChange}
                  type="number"
                  sx={{ backgroundColor: "white" }}
                />
              )}
              <LoadingButton
                variant="contained"
                loading={isSubmitting}
                disabled={!(isValid && dirty)}
                type="submit"
                sx={{ marginTop: "20px" }}
              >
                Continue
              </LoadingButton>
            </Box>
          </Form>
        </FormikProvider>
      </Box>
    </Slide>
  );
};
